import { InformationCircleIcon } from '@heroicons/react/20/solid';
import * as React from 'react';

import { TimelineEventRouter } from '@/components/tasks/task-detail/timeline/TimelineEventRouter';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useGetCoverageActivityQuery } from '@/services/api/coverageService';
import type { TaskActivity } from '@/services/api/generated/taskActivityService';

interface CoverageActivitySheetProps {
  matterId: string;
  coverageId: string;
  carrierName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CoverageActivitySheet: React.FC<CoverageActivitySheetProps> = ({
  matterId,
  coverageId,
  carrierName,
  open,
  onOpenChange,
}) => {
  const { data, isLoading, error } = useGetCoverageActivityQuery(
    { matterId, coverageId },
    { skip: !open },
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Coverage Activity</SheetTitle>
          <SheetDescription>
            {carrierName
              ? `Event history for ${carrierName}`
              : 'Event history for this coverage'}
          </SheetDescription>
        </SheetHeader>

        <div className="divide-y divide-slate-200 mt-2">
          {error && (
            <div className="text-sm text-red-600 py-4 px-4">
              Failed to load coverage activity
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-pulse text-slate-400 text-sm">
                Loading activity...
              </div>
            </div>
          )}

          {!isLoading && !error && data?.length === 0 && (
            <div className="py-8 flex flex-col items-center justify-center text-gray-300">
              <div className="size-10 rounded-full bg-gray-50 flex items-center justify-center mb-1">
                <InformationCircleIcon className="size-5 text-gray-200" />
              </div>
              <p className="text-xs font-medium text-gray-400">
                No activity yet
              </p>
            </div>
          )}

          {!isLoading &&
            !error &&
            data &&
            data.length > 0 &&
            data.map((item, idx) => (
              <TimelineEventRouter
                key={`coverage-event-${idx}`}
                activityItem={item as unknown as TaskActivity}
                matterId={matterId}
              />
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
